import React from 'react';
import { motion } from 'framer-motion';
import { GameState } from '@/types/career-sim';
import { Battery, Brain, Sparkles, DollarSign, Users, AlertTriangle } from 'lucide-react';

interface HorizontalStatsBarProps {
  gameState: GameState;
}

const HorizontalStatsBar: React.FC<HorizontalStatsBarProps> = ({ gameState }) => {
  const { needs, career, environment } = gameState;

  const getStatColor = (value: number) => {
    if (value >= 70) return 'bg-green-500';
    if (value >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatTextColor = (value: number) => {
    if (value >= 70) return 'text-green-400';
    if (value >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const StatBar = ({ icon: Icon, label, value, max = 100, color }: any) => {
    const percentage = (value / max) * 100;
    const isLow = value < 30;

    return (
      <div className="flex items-center gap-2 min-w-[140px]">
        <Icon className={`h-4 w-4 ${getStatTextColor(value)}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-white/70">{label}</span>
            <span className={`text-xs font-semibold ${getStatTextColor(value)}`}>
              {Math.round(value)}%
              {isLow && <AlertTriangle className="inline h-3 w-3 ml-1 animate-pulse" />}
            </span>
          </div>
          <div className="h-1.5 bg-black/40 rounded-full overflow-hidden border border-white/10">
            <motion.div
              className={`h-full ${color || getStatColor(value)}`}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>
    );
  };

  const CounterStat = ({ icon: Icon, label, value, trend }: any) => {
    return (
      <div className="flex items-center gap-2 bg-black/30 px-3 py-2 rounded-lg border border-white/10">
        <Icon className="h-4 w-4 text-neon-purple" />
        <div>
          <div className="text-xs text-white/70">{label}</div>
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-white">{value}</span>
            {trend && (
              <motion.span
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className={`text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}
              >
                {trend > 0 ? '+' : ''}{trend}
              </motion.span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-r from-black/60 via-black/50 to-black/60 backdrop-blur-md border-b border-white/10 px-6 py-4">
      {/* Top row - Week and Level info */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="text-white/50">Week</span>{' '}
            <span className="text-white font-bold">{environment.time.week}</span>
            <span className="text-white/50 mx-2">•</span>
            <span className="text-white/50">Day</span>{' '}
            <span className="text-white font-bold">{environment.time.day}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-gradient-to-r from-neon-purple/20 to-neon-blue/20 px-4 py-1.5 rounded-full border border-neon-purple/30">
          <span className="text-sm text-white/70">Level</span>
          <span className="text-lg font-bold text-white">{career.artistLevel}</span>
          <span className="text-xs text-white/50 capitalize">• {career.careerStage}</span>
        </div>
      </div>

      {/* Bottom row - Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-center">
        {/* Needs bars */}
        <StatBar icon={Battery} label="Energy" value={needs.energy} />
        <StatBar icon={Brain} label="Mental" value={needs.mentalHealth} />
        <StatBar icon={Sparkles} label="Creativity" value={needs.creativity} />

        {/* Counter stats */}
        <CounterStat 
          icon={DollarSign} 
          label="Funds" 
          value={`$${career.funds}`}
        />
        <CounterStat 
          icon={Users} 
          label="Fans" 
          value={career.fanbase}
        />
        <CounterStat 
          icon={Sparkles} 
          label="Reputation" 
          value={career.reputation}
        />
      </div>
    </div>
  );
};

export default HorizontalStatsBar;
